import { z } from 'zod';
import { IsoTime } from './basics.js';
import { WazuhMode } from './wazuh-agent.js';
import { DbTarget } from './db-target.js';
/**
 * What a server can actually do, reported in every heartbeat.
 *
 * Section 20 makes this load-bearing rather than cosmetic: three decisions
 * (Nginx package source, Cloudflare plan, and which web servers are installed)
 * are not knowable in advance and vary across the fleet, so they are answered by
 * detection and reported here. React shows only the actions a given server
 * supports, which is why this schema exists in phase 0 and not phase 6.
 */
/** Nginx has `limit_req`; Apache has no built-in request limiting (section 8.8). */
export const WebRateLimitCapability = z.enum(['none', 'nginx', 'crowdsec_scenario']);
/**
 * In Cloudflare and load balancer modes a firewall ban cannot affect HTTP,
 * because the firewall only ever sees the proxy's address.
 */
export const WebIpBanCapability = z.enum([
    'firewall',
    'nginx_bouncer',
    'apache_bouncer',
    'cloudflare',
    'none',
]);
export const WebCapabilities = z.object({
    uaBlock: z.boolean(),
    rateLimit: WebRateLimitCapability,
    webIpBan: WebIpBanCapability,
    /** Needs a challenge-capable bouncer: the Lua-based Nginx one, or a Cloudflare Worker. */
    challenge: z.boolean(),
});
/**
 * CrowdSec on this server, as the agent's `crowdsec` module last found it. A ban
 * from the dashboard is a CrowdSec decision, so a server without it is skipped
 * rather than sent a command that cannot succeed.
 */
export const CrowdsecCapability = z.object({
    /**
     * The installer registered the agent's identities and the Local API accepted
     * them. False on a server installed with `--no-crowdsec`. A Local API that is
     * briefly down still counts as installed: the command waits for its expiry.
     */
    installed: z.boolean(),
});
/**
 * The Wazuh manager on this server, as the agent's `wazuh_fim` module last found
 * it (P7.2). P7.3's rescan and accept-change actions are Wazuh operations, so a
 * server without it is skipped rather than sent them.
 */
export const WazuhCapability = z.object({
    /**
     * Something is at `/var/ossec`. False on a server installed with `--no-wazuh`,
     * or one whose Wazuh is an agent reporting to a manager elsewhere.
     */
    installed: z.boolean(),
    /**
     * The agent can read the alerts — the installer's ACL is in place. False when it
     * is not: an upgrade that recreated the directory, or a Wazuh set up by hand.
     */
    readable: z.boolean(),
    /**
     * P11.8.2: `central` where a Wazuh agent reports to the central server's
     * manager, whose alerts this server's agent does not read; absent from agents
     * that predate it, which are `local`.
     */
    mode: WazuhMode.optional(),
});
/**
 * The server needs a reboot (P11.19): Debian and Ubuntu write
 * `/var/run/reboot-required` when an upgrade or a fix leaves the running system
 * behind what is installed. Reported only while it is true, so a heartbeat that
 * leaves it out is the answer "restarted, or never needed": the dashboard's badge
 * clears on the first beat after the reboot, which is how it learns the restart
 * happened without anything being sent for it.
 */
export const RestartCapability = z.object({
    required: z.literal(true),
    /** When the file was written, as the server's clock has it. */
    since: IsoTime.optional(),
    /** The packages that asked for it, from `reboot-required.pkgs`; a few names. */
    packages: z.array(z.string().min(1).max(100)).max(20).default([]),
});
/** Absent sections mean the server has no capability of that kind at all. */
export const Capabilities = z.object({
    web: WebCapabilities.optional(),
    crowdsec: CrowdsecCapability.optional(),
    wazuh: WazuhCapability.optional(),
    /** Present only while the server wants a reboot. */
    restart: RestartCapability.optional(),
    /**
     * Databases this server's installer created a monitoring account for, from its
     * manifest (`DatabaseManifest`). What the server is ready to monitor rather than
     * something it can be told to do, and the same kind of answer: detected on the
     * server, reported, and turned into a choice in React — here, a target to add.
     */
    databases: z.array(DbTarget).max(20).optional(),
});
//# sourceMappingURL=capabilities.js.map